import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  Tag,
  TagColorSchemes,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment-timezone'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import CollapsibleMiddleBox from 'src/components/CollapsibleMiddleBox/CollapsibleMiddleBox'
import CustomerSupportCode from 'src/components/CustomerSupportCode/CustomerSupportCode'
import CustomerSupportCodeTooltip from 'src/components/CustomerSupportCodeTooltip/CustomerSupportCodeTooltip'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { UserListModel } from 'src/models/user.models'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import {
  accountOverviewUrl,
  budgetOverviewUrl,
  peopleMembersDetailUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { orgReceiptRulesQueries } from 'src/queries/organizations/receiptRules/receiptRules.queries'
import { usePutVirtualCardSupportCode } from 'src/queries/virtualCards.queries'
import { getOrganizationCardApprovalsEnabled } from 'src/redux/actions/data/organizations.actions'
import { getUser } from 'src/redux/actions/data/users.actions'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCardApprovalsFromState,
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { toDollars } from 'src/utils/currency.utils'
import styled, { css, CSSProp } from 'styled-components'

const UserLabelSpan = styled.span`
  ${getFontCss(
    12,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.medium,
  )};
`

const UserNameSpan = styled.span<{ cardCreator?: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )};

  ${({ cardCreator }): CSSProp => {
    return cardCreator
      ? css`
          ${getFontCss(
            16,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
        `
      : css``
  }}
`

const InfoStackDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 36px;

  ${getBreakpointDown(breakpointSizes.XSM)(css`
    margin-top: 36px;
  `)}

  &:last-child {
    margin-bottom: 36px;
  }

  ${getBreakpointDown(breakpointSizes.XSM)(css`
    &:last-child {
      margin-bottom: 36px;
    }
  `)}
`

const InfoHeaderSpan = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
`

const InfoBodySpan = styled.span<{ isActive?: boolean; isInactive?: boolean }>`
  margin-top: 4px;

  ${({ isActive }): CSSProp => {
    return isActive
      ? css`
          ${getFontCss(
            16,
            themeConstants.colors.highlight,
            styleConstants.fontFamilies.medium,
          )};
        `
      : css``
  }}

  ${({ isInactive }): CSSProp => {
    return isInactive
      ? css`
          ${getFontCss(
            16,
            styleConstants.colors.red,
            styleConstants.fontFamilies.medium,
          )};
        `
      : css``
  }}
`

interface AdditionalVirtualCardDetailsBoxProps {
  className?: string
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions?: VirtualCardPermissionsModel
  sender: UserListModel
  recipient: UserListModel
  creditCard?: CreditCardListModel
  pendingFinalApproval?: boolean
}

const AdditionalVirtualCardDetailsBox: React.FC<
  AdditionalVirtualCardDetailsBoxProps
> = ({
  className,
  recipient,
  sender,
  virtualCard,
  virtualCardPermissions,
  creditCard,
  pendingFinalApproval,
}): React.ReactElement => {
  const dispatch = useBackgroundDispatch()

  const {
    mutate: putVirtualCardSupportCode,
    data: customerSupportCodeResult,
    error: customerSupportCodeError,
    isPending: customerSupportCodeLoading,
  } = usePutVirtualCardSupportCode()
  const userId = useSelector(selectUserIdFromState)
  const { analytics } = useAnalyticsContext()
  const users = useSelector(selectUserListModelsFromState)
  const orgReceiptRulesEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: organization?.id,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)

  const translate = useTranslate()

  const isVirtualCardRecipient = userId === virtualCard.recipientId
  const cardSenderOrFinalApprover =
    cardApprovalRules && pendingFinalApproval
      ? cardApprovalRules.approverId
      : sender.id
  const isGuestUser =
    showGuestRole &&
    isVirtualCardRecipient &&
    recipient.organizationRole === OrganizationRoles.guest

  const orgReceiptRulesQuery = useQuery({
    ...orgReceiptRulesQueries.detail(organization?.id),
    enabled: Boolean(organization?.id) && orgReceiptRulesEnabled,
  })
  const orgReceiptRule = orgReceiptRulesQuery.data?.receiptRule

  useEffect((): void => {
    if (organization?.id !== undefined) {
      dispatch(getOrganizationCardApprovalsEnabled(organization.id))
    }
  }, [])

  useEffect((): void => {
    if (cardApprovalRules?.approverId) {
      dispatch(getUser(cardApprovalRules.approverId))
    }
  }, [cardApprovalRules])

  const topContent = (
    <div
      css={css`
        display: grid;
        grid-template-rows: auto auto;
        padding: 40px 40px;
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          padding: 36px 20px;
        `)}
      `}
      data-testid="additionalVirtualCardDetailsTopContent"
    >
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          grid-column: 1 / -1;
        `}
      >
        {translate('Additional Card Details')}
      </span>
      {!virtualCard.isPending && (
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(2, auto);
            align-items: flex-start;
            margin-top: 36px;
            ${getBreakpointDown(breakpointSizes.MD)(css`
              grid-template-columns: auto;
            `)}
            ${getBreakpointDown(breakpointSizes.SM)(css`
              grid-template-columns: repeat(2, auto);
            `)}
          ${getBreakpointDown(breakpointSizes.XSM)(css`
              grid-template-columns: auto;
            `)}
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
            `}
          >
            <UserAvatar
              size={AvatarSizes.Large}
              user={isVirtualCardRecipient ? sender : recipient}
            />
            <div
              css={css`
                margin-left: 8px;
                padding-right: 12px;
                display: flex;
                flex-direction: column;
                flex-wrap: nowrap;
                min-width: 0;
              `}
            >
              <UserLabelSpan>
                {isVirtualCardRecipient
                  ? translate('Card Creator')
                  : translate('Card User')}
              </UserLabelSpan>
              {isVirtualCardRecipient ? (
                <span>
                  {translate('${user}, on ${date}', {
                    user: (
                      <UserNameSpan
                        cardCreator
                        onClick={(): void => {
                          analytics?.track(
                            'card detail additional details - open user detail - text press',
                            {
                              screen: `/virtualCards/${virtualCard.id}`,
                              interaction: 'text press',
                              intent: 'open user detail',
                            },
                          )
                        }}
                        onKeyPress={(): null => {
                          return null
                        }}
                        tabIndex={0}
                      >
                        {isGuestUser ? (
                          <span
                            css={css`
                              ${getFontCss(
                                16,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                            data-testid={'cardCreator'}
                          >
                            {sender.truncatedFullNameOrEmail}
                          </span>
                        ) : (
                          <HoverableLink
                            size={HoverableLinkSizes.Large}
                            underline
                            to={peopleMembersDetailUrl(sender.id)}
                            ariaLabelSource={sender.truncatedFullNameOrEmail}
                            testId={'cardCreator'}
                          >
                            {sender.truncatedFullNameOrEmail}
                          </HoverableLink>
                        )}
                      </UserNameSpan>
                    ),
                    date: (
                      <span data-testid={'createdAtDate'}>
                        {virtualCard.createdAtTimezonedString}
                      </span>
                    ),
                  })}
                </span>
              ) : recipient.hasFullName ? (
                <span
                  css={css`
                    display: flex;
                    flex-direction: column;
                  `}
                >
                  {translate('${fullName}', {
                    fullName:
                      sender !== undefined ? (
                        <UserNameSpan
                          cardCreator
                          onClick={(): void => {
                            analytics?.track(
                              'card detail additional details - open user detail - text press',
                              {
                                screen: `/virtualCards/${virtualCard.id}`,
                                interaction: 'text press',
                                intent: 'open user detail',
                              },
                            )
                          }}
                          onKeyPress={(): null => {
                            return null
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <HoverableLink
                            size={HoverableLinkSizes.Large}
                            underline
                            to={peopleMembersDetailUrl(recipient.id)}
                            ariaLabelSource={sender.truncatedFullNameOrEmail}
                            testId={'cardUser'}
                          >
                            {recipient.fullNameOrEmail}
                          </HoverableLink>
                        </UserNameSpan>
                      ) : (
                        <UserNameSpan data-testid={'cardUser'}>
                          {recipient.fullNameOrEmail}
                        </UserNameSpan>
                      ),
                  })}
                  <span
                    css={css`
                      overflow: hidden;
                      white-space: nowrap;
                      text-overflow: ellipsis;
                    `}
                    data-testid={'cardUserEmail'}
                  >
                    {translate(' (${email})', {
                      email: recipient.email,
                    })}
                  </span>
                </span>
              ) : (
                <span>
                  {translate('${email} (unregistered)', {
                    email: (
                      <span
                        onClick={(): void => {
                          analytics?.track(
                            'card detail additional details - open user detail - text press',
                            {
                              screen: `/virtualCards/${virtualCard.id}`,
                              interaction: 'text press',
                              intent: 'open user detail',
                            },
                          )
                        }}
                        onKeyPress={(): null => {
                          return null
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <HoverableLink
                          size={HoverableLinkSizes.Large}
                          underline
                          to={peopleMembersDetailUrl(recipient.id)}
                          ariaLabelSource={sender.truncatedFullNameOrEmail}
                          testId={'cardUser-unregistered'}
                        >
                          {recipient.email}
                        </HoverableLink>
                      </span>
                    ),
                  })}
                </span>
              )}
            </div>
          </div>
          {/* `creditCard` assumes that if the creditCard prop is undefined, the user does not have access to it */}
          {virtualCard?.creditCardId &&
            virtualCard.creditCardDisplayName &&
            creditCard && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                  margin-left: 40px;
                  ${getBreakpointDown(breakpointSizes.MD)(css`
                    margin-left: 48px;
                    margin-top: 20px;
                  `)}
                `}
              >
                <UserLabelSpan>{translate('Account')}</UserLabelSpan>
                <HoverableLink
                  css={css`
                    overflow-wrap: break-word;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                  `}
                  underline
                  to={
                    creditCard?.isBudget
                      ? budgetOverviewUrl(virtualCard.creditCardId)
                      : accountOverviewUrl(virtualCard.creditCardId)
                  }
                  ariaLabelSource={virtualCard.creditCardDisplayName}
                  size={HoverableLinkSizes.Large}
                  testId={'virtualCardAccountLink'}
                >
                  {virtualCard.creditCardDisplayName}
                </HoverableLink>
              </div>
            )}
          {virtualCard !== undefined &&
            !virtualCard.direct &&
            orgReceiptRulesEnabled &&
            orgReceiptRule?.enabled && (
              <div
                css={css`
                  margin-top: 20px;
                  margin-left: 48px;
                `}
              >
                <UserLabelSpan>{translate('Receipt Rules')}</UserLabelSpan>
                {!virtualCard.receiptRulesExempt ? (
                  <div data-testid="receiptRulesTag" data-isrequired="true">
                    <Tag
                      text={translate('Receipts Required')}
                      color={TagColorSchemes.Green}
                    />
                  </div>
                ) : (
                  <div data-testid="receiptRulesTag" data-isrequired="false">
                    <Tag
                      text={translate('Receipts Not Required')}
                      color={TagColorSchemes.Default}
                    />
                  </div>
                )}
              </div>
            )}
        </div>
      )}
    </div>
  )

  const middleContent = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 0px 40px;

        ${getBreakpointDown(breakpointSizes.XXSM)(css`
          padding: 0px 20px;
        `)}
      `}
    >
      {virtualCard.isBillPay && (
        <>
          <InfoStackDiv>
            <InfoHeaderSpan>{translate('Invoice Number')}</InfoHeaderSpan>
            <span data-testid={'invoiceNumber'}>
              {virtualCard.billPayMeta?.invoiceNumber ?? '--'}
            </span>
          </InfoStackDiv>
          <InfoStackDiv>
            <InfoHeaderSpan>{translate('Invoice Date')}</InfoHeaderSpan>
            <span data-testid={'invoiceDate'}>
              {virtualCard.invoiceDateTimezonedString ?? '--'}
            </span>
          </InfoStackDiv>
        </>
      )}
      <InfoStackDiv>
        <InfoHeaderSpan>{translate('Status Details')}</InfoHeaderSpan>
        <InfoBodySpan
          isActive={virtualCard.pseudoStatusModifiers.isActive}
          isInactive={virtualCard.pseudoStatusModifiers.isInactive}
          data-testid={'cardStatus'}
        >
          {virtualCard.isPending
            ? translate('${pendingApproval} by ${cardCreator}', {
                pendingApproval: (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.gray_dark,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate(
                      pendingFinalApproval
                        ? 'Pending Final Approval'
                        : 'Pending Approval',
                    )}
                  </span>
                ),
                cardCreator:
                  users[cardSenderOrFinalApprover] &&
                  (isGuestUser ? (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {
                        users[cardSenderOrFinalApprover]
                          .truncatedFullNameOrEmail
                      }
                    </span>
                  ) : (
                    <HoverableLink
                      underline
                      size={HoverableLinkSizes.Large}
                      to={peopleMembersDetailUrl(cardSenderOrFinalApprover)}
                      ariaLabelSource={
                        users[cardSenderOrFinalApprover].fullNameOrEmail
                      }
                    >
                      {users[cardSenderOrFinalApprover].fullNameOrEmail}
                    </HoverableLink>
                  )),
              })
            : virtualCard.psuedoStatusLabel}
        </InfoBodySpan>
      </InfoStackDiv>
      {virtualCard.isIssuerCycle && (
        <InfoStackDiv>
          <InfoHeaderSpan>{translate('Monthly Credit Limit')}</InfoHeaderSpan>
          <InfoBodySpan data-testid={'monthlyCreditLimit'}>
            <Crncy
              currency={virtualCard.currency}
              amount={toDollars(virtualCard.limitCents)}
              maximumFractionDigits={0}
            />
          </InfoBodySpan>
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-top: 4px;
            `}
          >
            {translate('The credit limit resets to this amount on ${date}.', {
              date: virtualCard.issuerCycleConfig?.nextAt
                ? moment(virtualCard.issuerCycleConfig.nextAt).format('ll')
                : '--',
            })}
          </span>
        </InfoStackDiv>
      )}
      <InfoStackDiv>
        <InfoHeaderSpan>
          {translate(
            virtualCard?.isBillPay ? 'Invoice Amount' : 'Credit Limit',
          )}
        </InfoHeaderSpan>
        <InfoBodySpan
          isActive={virtualCard.pseudoStatusModifiers.isActive}
          isInactive={virtualCard.pseudoStatusModifiers.isInactive}
        >
          {virtualCard.isPending &&
            translate('${amount}, requested on ${date}', {
              amount: <span>{virtualCard.limitLabel}</span>,
              date: virtualCard.createdAtTimezonedString,
            })}
          {!virtualCard.isPending && (
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
              data-testid={
                virtualCard?.isBillPay ? 'invoiceAmount' : 'creditLimit'
              }
            >
              {virtualCard.isClosed ? '--' : virtualCard.limitLabel}
            </span>
          )}
        </InfoBodySpan>
      </InfoStackDiv>
      {virtualCard.isBillPay && (
        <InfoStackDiv>
          <InfoHeaderSpan>{translate('Vendor Name')}</InfoHeaderSpan>
          <span data-testid={'vendorName'}>
            {virtualCard.billPayMeta?.vendorName ?? '--'}
          </span>
        </InfoStackDiv>
      )}
      {virtualCard.isBillPay && (
        <InfoStackDiv>
          <InfoHeaderSpan>{translate('Vendor Email')}</InfoHeaderSpan>
          <span data-testid={'vendorEmail'}>
            {virtualCard.billPayVendorEmail ?? '--'}
          </span>
        </InfoStackDiv>
      )}
      {virtualCardPermissions?.canSpend && (
        <InfoStackDiv>
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              display: flex;
              align-items: center;
            `}
          >
            {translate('Customer Support Code')}
            <CustomerSupportCodeTooltip />
          </span>
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
            data-testid={'customerSupportCode'}
          >
            <CustomerSupportCode
              handleClick={(): void =>
                putVirtualCardSupportCode({
                  virtualCardId: virtualCard.id,
                  body: {},
                })
              }
              customerSupportCode={
                customerSupportCodeResult?.customerSupportCode
              }
              loading={customerSupportCodeLoading}
            />
          </div>
          {customerSupportCodeError && (
            <TopLevelError
              error={translate(
                'The customer support cord failed to generate. Please make sure you have a stable internet connection.',
              )}
              data-testid={'customerSupportCodeError'}
              css={css`
                margin-top: 20px;
              `}
            />
          )}
        </InfoStackDiv>
      )}
      {virtualCard.companyName !== undefined && (
        <InfoStackDiv>
          <InfoHeaderSpan>{translate('Company Name')}</InfoHeaderSpan>
          <span data-testid={'companyName'}>{virtualCard.companyName}</span>
        </InfoStackDiv>
      )}
    </div>
  )

  const closedBottomLabel = translate('More information')
  const openBottomLabel = translate('Less information')

  return (
    <CollapsibleMiddleBox
      className={className || 'virtualCardAdditionalDetails'}
      topContent={topContent}
      middleContent={middleContent}
      bottomContent={{
        closedBottomLabel: closedBottomLabel,
        openBottomLabel: openBottomLabel,
      }}
    />
  )
}

export default AdditionalVirtualCardDetailsBox
